import { useQuery as useConvexQuery } from "convex/react";
import { useAuth } from "@clerk/clerk-react";
import { api } from "../../api";
import { type ConvexUser } from "@/types/convex";
import { useState, useEffect } from "react";

export const useCurrentUser = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const [error, setError] = useState<string | null>(null);
  
  // Debug authentication state
  console.log("Auth state:", { isSignedIn, isLoaded });
  
  // Use a more defensive approach - always skip if there might be auth issues
  // We'll only attempt the query if we're confident about authentication
  const shouldQuery = isLoaded && isSignedIn && !error;
  
  const currentUser = useConvexQuery(
    api.users.get.me, 
    shouldQuery ? {} : "skip"
  );
  
  // Debug Convex query result
  console.log("Convex query result:", { currentUser, shouldQuery });
  
  // Reset error when auth state changes
  useEffect(() => {
    if (!isSignedIn) {
      setError(null);
    }
  }, [isSignedIn]);
  
  // Handle loading states
  if (!isLoaded) {
    return {
      user: undefined,
      isLoading: true,
      isAuthenticated: false,
      error: null,
    };
  }
  
  if (!isSignedIn) {
    return {
      user: null,
      isLoading: false,
      isAuthenticated: false,
      error: null,
    };
  }
  
  // If there's an authentication error, return it
  if (error) {
    return {
      user: null,
      isLoading: false,
      isAuthenticated: true,
      error,
    };
  }
  
  // If we're signed in but the query is still loading
  if (currentUser === undefined) {
    return {
      user: undefined,
      isLoading: true,
      isAuthenticated: true,
      error: null,
    };
  }
  
  // If the query returned null, it means user doesn't exist in Convex
  if (currentUser === null) {
    return {
      user: null,
      isLoading: false,
      isAuthenticated: true,
      error: "User not found in database",
    };
  }
  
  // Success case
  return {
    user: currentUser,
    isLoading: false,
    isAuthenticated: true,
    error: null,
  };
};